import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isInitialQueryLoading } from '../../../lib/query/listQuery';
import { usePagedListUrlState } from '../../../lib/query/usePagedListUrlState';
import { useUrlListFilter, serializeValue } from '../../../lib/query/useUrlListFilter';
import {
  Button,
  Select,
  Tag,
  Input,
  Dropdown,
  Tooltip,
  DatePicker,
  Space,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { MenuProps } from 'antd';
import {
  PlusOutlined,
  LinkOutlined,
  DownOutlined,
  PhoneOutlined,
  CheckSquareOutlined,
  SyncOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { appPath } from '../../../routes/paths';
import { TASK_FILTER_FIELDS } from '../../../routes/filterMaps';
import type { ColumnsType } from 'antd/es/table';
import DataTable from '../../../components/DataTable';
import { ListPage, FilterBar } from '../../../components/list';
import TaskFormDrawer from './TaskFormDrawer';
import TaskDetailDrawer from './TaskDetailDrawer';
import LeadViewDrawer from '../../leads/components/LeadViewDrawer';
import TaskActivityPrimaryCell from './TaskActivityPrimaryCell';
import TaskActivityRowActions from './TaskActivityRowActions';
import ResolveFollowupModal from './ResolveFollowupModal';
import SnoozeFollowupModal from './SnoozeFollowupModal';
import CompleteRecurringModal from './CompleteRecurringModal';

import { useTaskItems } from '../hooks/useTaskItems';
import { useResolveFollowupTask } from '../hooks/useResolveFollowupTask';
import { useSnoozeFollowupTask } from '../hooks/useSnoozeFollowupTask';
import { useCompleteRecurringTask } from '../hooks/useCompleteRecurringTask';
import { useUpdateTaskStatus } from '../hooks/useUpdateTaskStatus';
import { useDeleteTaskItem } from '../hooks/useDeleteTaskItem';
import { useUserOptions } from '../../../hooks/options/useUserOptions';
import { useContactOptions } from '../../../hooks/options/useContactOptions';
import { useUserSegmentScope } from '../../auth/hooks/useUserSegmentScope';
import { useCustomerSegmentOptions } from '../../../hooks/options/useCustomerSegmentOptions';
import { useContactCategories } from '../../contacts/hooks/useContactCategories';
import { contactDisplayName } from '../../contacts/contactType';
import type { TaskItemListItem, TaskItemStatus, TaskPriority, TaskType, ExportTasksParams, GetTaskItemsParams } from '../types';
import { useExportTasks } from '../hooks/useExportTasks';
import { useExportTasksPdf } from '../hooks/useExportTasksPdf';
import {
  TASK_STATUS_OPTIONS,
  TASK_PRIORITY_OPTIONS,
  TASK_TYPE_FILTER_OPTIONS,
  TASK_TYPE_FOLLOWUP,
  TASK_TYPE_INTERNAL,
  TASK_TYPE_RECURRING,
} from '../constants';
import {
  ACTIVITIES_PAGE_TITLE,
  ACTIVITIES_PAGE_SUBTITLE,
  ACTIVITIES_NEW_BUTTON,
  ACTIVITIES_NEW_FOLLOWUP,
  ACTIVITIES_NEW_INTERNAL,
  ACTIVITIES_NEW_RECURRING,
  ACTIVITIES_EMPTY_DEFAULT,
  ACTIVITIES_EMPTY_FILTERED,
  ACTIVITIES_SEARCH_PLACEHOLDER,
} from '../copy';
import Can from '../../permissions/Can';
import {
  activityDueIsOverdue,
  activityDueIsToday,
  formatActivityTableDate,
} from '../taskActivityTableFormat';

const { RangePicker } = DatePicker;

const ACTIONS_COLUMN_WIDTH = 56;

interface TasksListFilterState {
  statusFilter?: TaskItemStatus;
  priorityFilter?: TaskPriority;
  assigneeFilter?: string;
  contactFilter?: string;
  taskTypeFilter?: TaskType;
  isOverdue?: boolean;
  completedLate?: boolean;
  searchText: string;
  segmentIds?: string[];
  contactCategoryIds?: number[];
  dueFrom?: string;
  dueTo?: string;
  createdFrom?: string;
  createdTo?: string;
  batchNameFilter?: string;
}

const defaultTasksFilter: TasksListFilterState = {
  searchText: '',
};

const TasksPage: React.FC = () => {
  const { page, pageSize, setPage, setPageSize } = usePagedListUrlState(10);
  const [listFilter, setListFilter] = useUrlListFilter(defaultTasksFilter, TASK_FILTER_FIELDS);
  const {
    statusFilter,
    priorityFilter,
    assigneeFilter,
    contactFilter,
    taskTypeFilter,
    isOverdue,
    completedLate,
    searchText,
    dueFrom,
    dueTo,
    createdFrom,
    createdTo,
    batchNameFilter,
  } = listFilter;
  const { isScoped, assignedSegmentIds } = useUserSegmentScope();
  const { data: segments = [] } = useCustomerSegmentOptions({ activeOnly: true });
  const { data: categoriesData = [] } = useContactCategories();
  const segmentFilterOptions = useMemo(() => {
    if (!isScoped) {
      return segments.map((s) => ({ value: s.id, label: s.label }));
    }
    const allowed = new Set(assignedSegmentIds);
    return segments.filter((s) => allowed.has(s.id)).map((s) => ({ value: s.id, label: s.label }));
  }, [isScoped, assignedSegmentIds, segments]);
  const contactCategoryOptions = useMemo(
    () => categoriesData.filter((c) => c.isActive && c.id > 0).map((c) => ({ value: c.id, label: c.label })),
    [categoriesData],
  );

  const [searchInput, setSearchInput] = useState(searchText);

  useEffect(() => {
    const timer = setTimeout(() => {
      setListFilter((prev) => ({ ...prev, searchText: searchInput }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, setListFilter]);

  const dueRange: [Dayjs | null, Dayjs | null] | null = useMemo(() => {
    if (dueFrom && dueTo) return [dayjs(dueFrom), dayjs(dueTo)];
    return null;
  }, [dueFrom, dueTo]);

  const createdRange: [Dayjs | null, Dayjs | null] | null = useMemo(() => {
    if (createdFrom && createdTo) return [dayjs(createdFrom), dayjs(createdTo)];
    return null;
  }, [createdFrom, createdTo]);

  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [newTaskType, setNewTaskType] = useState<TaskType>(TASK_TYPE_FOLLOWUP);
  const [editTaskId, setEditTaskId] = useState<string | null>(null);
  const [viewTaskId, setViewTaskId] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [resolveTaskId, setResolveTaskId] = useState<string | null>(null);
  const [completeRecurringTaskId, setCompleteRecurringTaskId] = useState<string | null>(null);
  const [snoozeTaskId, setSnoozeTaskId] = useState<string | null>(null);
  const navigate = useNavigate();



  const params: GetTaskItemsParams = useMemo(
    () => ({
      status: statusFilter,
      priority: priorityFilter,
      assigneeId: assigneeFilter,
      contactId: contactFilter,
      taskType: taskTypeFilter,
      search: searchText || undefined,
      isOverdue,
      completedLate,
      dueFrom,
      dueTo,
      createdFrom,
      createdTo,
      segmentIds: listFilter.segmentIds,
      contactCategoryIds: listFilter.contactCategoryIds,
      batchName: batchNameFilter || undefined,
      pageNumber: page,
      pageSize,
    }),
    [
      statusFilter,
      priorityFilter,
      assigneeFilter,
      contactFilter,
      taskTypeFilter,
      isOverdue,
      completedLate,
      searchText,
      dueFrom,
      dueTo,
      createdFrom,
      createdTo,
      listFilter.segmentIds,
      listFilter.contactCategoryIds,
      batchNameFilter,
      page,
      pageSize,
    ]
  );

  const { data: paged, isLoading: taskItemsLoading } = useTaskItems(params);
  const isLoading = isInitialQueryLoading(taskItemsLoading, paged);
  const { data: users = [] } = useUserOptions();
  const { data: contacts = [] } = useContactOptions();
  const statusMutation = useUpdateTaskStatus();
  const deleteMutation = useDeleteTaskItem();
  const resolveMutation = useResolveFollowupTask(() => setResolveTaskId(null));
  const completeRecurringMutation = useCompleteRecurringTask(() => setCompleteRecurringTaskId(null));
  const snoozeMutation = useSnoozeFollowupTask(() => setSnoozeTaskId(null));
  const exportMutation = useExportTasks();
  const pdfExportMutation = useExportTasksPdf();

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: u.label,
      })),
    [users]
  );

  const contactOptions = useMemo(
    () =>
      contacts.map((c) => ({
        value: c.id,
        label: contactDisplayName(c),
      })),
    [contacts]
  );

  const userLookup = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((u) => map.set(u.id, u.label));
    return map;
  }, [users]);

  const contactLookup = useMemo(() => {
    const map = new Map<string, { name: string }>();
    contacts.forEach((c) =>
      map.set(c.id, { name: contactDisplayName(c) })
    );
    return map;
  }, [contacts]);

  const openAdd = (taskType: TaskType) => {
    setNewTaskType(taskType);
    setEditTaskId(null);
    setFormDrawerOpen(true);
  };

  const handleView = (id: string) => {
    setViewTaskId(id);
  };

  const handleEdit = (id: string) => {
    setEditTaskId(id);
    setFormDrawerOpen(true);
  };

  const handleDelete = (id: string) => deleteMutation.mutate(id);

  const handleStatusChange = (id: string, status: TaskItemStatus) => {
    statusMutation.mutate({ id, status });
  };

  const handleFormSuccess = () => {
    setFormDrawerOpen(false);
    setEditTaskId(null);
  };

  const [, rawSetSearchParams] = useSearchParams();

  const patchListFilter = useCallback(
    (patch: Partial<TasksListFilterState>) => {
      const merged = { ...listFilter, ...patch };
      rawSetSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.delete('page');
          for (const def of TASK_FILTER_FIELDS) {
            const val = (merged as Record<string, unknown>)[def.field];
            const serialized = serializeValue(val, def);
            if (serialized !== undefined) {
              nextParams.set(def.param, serialized);
            } else {
              nextParams.delete(def.param);
            }
          }
          return nextParams;
        },
        { replace: true },
      );
    },
    [listFilter, rawSetSearchParams],
  );

  const hasActiveFilters =
    Boolean(searchText) ||
    statusFilter !== undefined ||
    priorityFilter !== undefined ||
    assigneeFilter !== undefined ||
    contactFilter !== undefined ||
    taskTypeFilter !== undefined ||
    isOverdue !== undefined ||
    Boolean(dueFrom || dueTo) ||
    Boolean(createdFrom || createdTo) ||
    Boolean(batchNameFilter) ||
    (listFilter.segmentIds !== undefined && listFilter.segmentIds.length > 0) ||
    (listFilter.contactCategoryIds !== undefined && listFilter.contactCategoryIds.length > 0);

  const newActivityMenu: MenuProps['items'] = [
    {
      key: 'followup',
      label: ACTIVITIES_NEW_FOLLOWUP,
      icon: <PhoneOutlined />,
      onClick: () => openAdd(TASK_TYPE_FOLLOWUP),
    },
    {
      key: 'internal',
      label: ACTIVITIES_NEW_INTERNAL,
      icon: <CheckSquareOutlined />,
      onClick: () => openAdd(TASK_TYPE_INTERNAL),
    },
    {
      key: 'recurring',
      label: ACTIVITIES_NEW_RECURRING,
      icon: <SyncOutlined />,
      onClick: () => openAdd(TASK_TYPE_RECURRING),
    },
  ];

  const columns: ColumnsType<TaskItemListItem> = [
    {
      title: 'SN',
      key: 'sn',
      width: 56,
      fixed: 'left',
      align: 'center',
      render: (_, __, index) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {(page - 1) * pageSize + index + 1}
        </Typography.Text>
      ),
    },
    {
      title: 'Contact',
      dataIndex: 'contactId',
      key: 'contactId',
      width: 150,
      ellipsis: true,
      render: (contactId: string | null) =>
        contactId ? (
          <a onClick={() => navigate(appPath(`/kundali/${contactId}`))}>
            {contactLookup.get(contactId)?.name ?? '—'} <LinkOutlined />
          </a>
        ) : (
          '—'
        ),
    },
    {
      title: 'Assignee',
      dataIndex: 'assigneeId',
      key: 'assigneeId',
      width: 140,
      render: (assigneeId: string | null, record) =>
        assigneeId
          ? userLookup.get(assigneeId) ?? (record as TaskItemListItem).assigneeDisplayName ?? '—'
          : '—',
    },
    {
      title: 'Issue',
      dataIndex: 'createdAtUtc',
      key: 'createdAtUtc',
      width: 110,
      render: (date: string | undefined) => formatActivityTableDate(date),
    },
    {
      title: 'Due',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (date: string | null, record) => {
        const overdue = activityDueIsOverdue(date, record.status);
        const dueToday = activityDueIsToday(date);
        const color = overdue ? 'error' : dueToday ? 'warning' : undefined;
        const label = formatActivityTableDate(date);
        return color ? (
          <Tooltip title={overdue ? 'Overdue' : 'Due today'}>
            <Tag color={color}>{label}</Tag>
          </Tooltip>
        ) : (
          <span>{label}</span>
        );
      },
    },
    {
      title: 'Completed',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 120,
      render: (date: string | null | undefined) => formatActivityTableDate(date),
    },
    {
      title: 'Activity',
      dataIndex: 'title',
      key: 'title',
      width: 260,
      render: (_, record) => (
        <TaskActivityPrimaryCell record={record} onViewTitle={handleView} />
      ),
    },
    {
      title: 'Batch Name',
      dataIndex: 'batchName',
      key: 'batchName',
      width: 140,
      ellipsis: true,
      render: (batchName: string | null) => batchName || 'Day to Day',
    },
    {
      title: 'Lead',
      dataIndex: 'leadTitle',
      key: 'leadTitle',
      width: 140,
      ellipsis: true,
      render: (leadTitle: string | null, record) =>
        record.leadId ? (
          <a onClick={() => setSelectedLeadId(record.leadId)}>
            {leadTitle ?? '—'} <LinkOutlined />
          </a>
        ) : (
          leadTitle ?? '—'
        ),
    },
    {
      title: '#',
      key: 'actions',
      width: ACTIONS_COLUMN_WIDTH,
      fixed: 'right',
      align: 'center',
      render: (_, record, index) => (
        <TaskActivityRowActions
          record={record}
          rowNumber={(page - 1) * pageSize + index + 1}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
          onResolve={setResolveTaskId}
          onCompleteRecurring={setCompleteRecurringTaskId}
          onSnooze={setSnoozeTaskId}
        />
      ),
    },
  ];

  const resetFilters = () => {
    setSearchInput('');
    rawSetSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('page');
        for (const def of TASK_FILTER_FIELDS) {
          nextParams.delete(def.param);
        }
        return nextParams;
      },
      { replace: true },
    );
  };

  const handleExportLeads = useCallback((format: 'excel' | 'pdf') => {
    const exportParams: ExportTasksParams = {
      status: statusFilter,
      priority: priorityFilter,
      assigneeId: assigneeFilter,
      contactId: contactFilter,
      taskType: taskTypeFilter,
      search: searchText || undefined,
      isOverdue,
      completedLate,
      dueFrom,
      dueTo,
      createdFrom,
      createdTo,
      segmentIds: listFilter.segmentIds,
      contactCategoryIds: listFilter.contactCategoryIds,
      batchName: batchNameFilter || undefined,
    };
    if (format === 'excel') {
      exportMutation.mutate(exportParams);
    } else {
      pdfExportMutation.mutate(exportParams);
    }
  }, [statusFilter, priorityFilter, assigneeFilter, contactFilter, taskTypeFilter, isOverdue, completedLate, searchText, dueFrom, dueTo, createdFrom, createdTo, listFilter.segmentIds, listFilter.contactCategoryIds, batchNameFilter, exportMutation, pdfExportMutation]);

  const exportMenuItems: MenuProps['items'] = [
    { key: 'excel', icon: <FileExcelOutlined />, label: 'Excel (.xlsx)' },
    { key: 'pdf', icon: <FilePdfOutlined />, label: 'PDF (.pdf)' },
  ];

  return (
    <>
      <ListPage
        title={ACTIVITIES_PAGE_TITLE}
        subtitle={ACTIVITIES_PAGE_SUBTITLE}
        header={null}
        actions={(
          <Space>
            <Can permission="tasks.view">
              <Dropdown
                menu={{ items: exportMenuItems, onClick: ({ key }) => handleExportLeads(key as 'excel' | 'pdf') }}
                trigger={['click']}
              >
                <Button
                  icon={<DownloadOutlined />}
                  loading={exportMutation.isPending || pdfExportMutation.isPending}
                >
                  Export
                </Button>
              </Dropdown>
            </Can>
            <Can permission="tasks.create">
              <Dropdown menu={{ items: newActivityMenu }} trigger={['click']}>
                <Button type="primary" icon={<PlusOutlined />}>
                  {ACTIVITIES_NEW_BUTTON} <DownOutlined />
                </Button>
              </Dropdown>
            </Can>
          </Space>
        )}
        filters={(
          <FilterBar onReset={resetFilters}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Title</span>
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={ACTIVITIES_SEARCH_PLACEHOLDER}
                style={{ width: 240 }}
                allowClear
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Status</span>
              <Select
                value={statusFilter}
                onChange={(val) => {
                  patchListFilter({ statusFilter: val });
                }}
                allowClear
                style={{ width: 150 }}
                options={TASK_STATUS_OPTIONS}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Priority</span>
              <Select
                value={priorityFilter}
                onChange={(val) => {
                  patchListFilter({ priorityFilter: val });
                }}
                allowClear
                style={{ width: 130 }}
                options={TASK_PRIORITY_OPTIONS}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Assignee</span>
              <Select
                value={assigneeFilter}
                onChange={(val) => {
                  patchListFilter({ assigneeFilter: val });
                }}
                allowClear
                showSearch
                style={{ width: 160 }}
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
                options={userOptions}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Contact</span>
              <Select
                value={contactFilter}
                onChange={(val) => {
                  patchListFilter({ contactFilter: val });
                }}
                allowClear
                showSearch
                style={{ width: 180 }}
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
                options={contactOptions}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Batch Name</span>
              <Input.Search
                value={batchNameFilter ?? ''}
                onChange={(e) => patchListFilter({ batchNameFilter: e.target.value || undefined })}
                onSearch={(val) => patchListFilter({ batchNameFilter: val || undefined })}
                style={{ width: 160 }}
                allowClear
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Type</span>
              <Select
                value={taskTypeFilter}
                onChange={(val) => {
                  patchListFilter({ taskTypeFilter: val });
                }}
                allowClear
                style={{ width: 170 }}
                placeholder="All types"
                options={TASK_TYPE_FILTER_OPTIONS}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Overdue</span>
              <Select
                value={isOverdue}
                onChange={(val) => {
                  patchListFilter({ isOverdue: val });
                }}
                allowClear
                style={{ width: 120 }}
                options={[
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' },
                ]}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Due Date</span>
              <RangePicker
                value={dueRange ?? undefined}
                onChange={(range) => {
                  patchListFilter({
                    dueFrom: range?.[0]?.format('YYYY-MM-DD') ?? undefined,
                    dueTo: range?.[1]?.format('YYYY-MM-DD') ?? undefined,
                  });
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Created Date</span>
              <RangePicker
                value={createdRange ?? undefined}
                onChange={(range) => {
                  patchListFilter({
                    createdFrom: range?.[0]?.format('YYYY-MM-DD') ?? undefined,
                    createdTo: range?.[1]?.format('YYYY-MM-DD') ?? undefined,
                  });
                }}
              />
            </div>
            {segmentFilterOptions.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Customer Segments</span>
                <Select<string[]>
                  mode="multiple"
                  allowClear
                  options={segmentFilterOptions}
                  value={listFilter.segmentIds}
                  onChange={(v) => patchListFilter({ segmentIds: v.length > 0 ? v : undefined })}
                  style={{ minWidth: 200 }}
                  optionFilterProp="label"
                  maxTagCount="responsive"
                />
              </div>
            ) : null}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Contact Categories</span>
              <Select<number[]>
                mode="multiple"
                allowClear
                options={contactCategoryOptions}
                value={listFilter.contactCategoryIds}
                onChange={(v) => patchListFilter({ contactCategoryIds: v.length > 0 ? v : undefined })}
                style={{ minWidth: 200 }}
                optionFilterProp="label"
                maxTagCount="responsive"
              />
            </div>
          </FilterBar>
        )}
      >
        <DataTable
          columns={columns}
          dataSource={paged?.items ?? []}
          loading={isLoading || statusMutation.isPending}
          rowKey="id"
          bordered
          striped
          scroll={{ x: 890 }}
          emptyText={
            hasActiveFilters
              ? ACTIVITIES_EMPTY_FILTERED
              : ACTIVITIES_EMPTY_DEFAULT
          }
          pagination={{
            current: page,
            pageSize,
            total: paged?.totalCount ?? 0,
            showSizeChanger: true,
            showTotal: (total) => `${total} activit${total === 1 ? 'y' : 'ies'}`,
            onChange: (p, ps) => {
              if (ps !== pageSize) {
                setPageSize(ps);
              } else {
                setPage(p);
              }
            },
          }}
        />
      </ListPage>

      <TaskFormDrawer
        open={formDrawerOpen}
        onClose={() => {
          setFormDrawerOpen(false);
          setEditTaskId(null);
        }}
        onSuccess={handleFormSuccess}
        editTaskId={editTaskId}
        defaultTaskType={newTaskType}
      />

      <TaskDetailDrawer
        open={!!viewTaskId}
        onClose={() => setViewTaskId(null)}
        taskId={viewTaskId}
      />

      <ResolveFollowupModal
        open={resolveTaskId != null}
        taskId={resolveTaskId}
        loading={resolveMutation.isPending}
        onCancel={() => setResolveTaskId(null)}
        onSubmit={(taskId, payload) => resolveMutation.mutate({ taskId, payload })}
      />

      <SnoozeFollowupModal
        open={snoozeTaskId != null}
        taskId={snoozeTaskId}
        loading={snoozeMutation.isPending}
        onCancel={() => setSnoozeTaskId(null)}
        onSubmit={(taskId, newDueDate, reason) =>
          snoozeMutation.mutate({ taskId, newDueDate, reason })
        }
      />

      <CompleteRecurringModal
        open={completeRecurringTaskId != null}
        taskId={completeRecurringTaskId}
        loading={completeRecurringMutation.isPending}
        onCancel={() => setCompleteRecurringTaskId(null)}
        onSubmit={(taskId, payload) => completeRecurringMutation.mutate({ taskId, payload })}
      />

      <LeadViewDrawer
        open={selectedLeadId != null}
        onClose={() => setSelectedLeadId(null)}
        leadId={selectedLeadId}
      />
    </>
  );
};

export default TasksPage;
